import { TopicError, type OriginalCommand, type OriginalReceipt, type OriginalPending } from './topicClient';

export type SavedOperation = { owner: string; body: OriginalCommand; receipt?: OriginalReceipt };
type StoragePort = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const actions = ['start', 'turn', 'assessment', 'complete', 'message', 'rebuttal'];
export function originalStorageKey(owner: string) {
  if (!/^[a-z0-9][a-z0-9_-]{1,79}$/.test(owner)) throw new TopicError('account_unconfirmed');
  return `analects-original-operation-v1:${owner}`;
}
function validCommand(body: OriginalCommand) {
  return body && actions.includes(body.action) && /^[A-Za-z0-9:_-]{12,160}$/.test(body.idempotencyKey)
    && (body.action === 'start' || (typeof body.sessionId === 'string' && body.sessionId.length > 0));
}
/** One unresolved operation per owner, stored before network dispatch. Failure
 * to durably read back a write blocks dispatch. Original whitespace is evidence. */
export class OriginalOperations {
  private storage: StoragePort;
  private owner: string;
  private key: string;
  constructor(owner: string, storage: StoragePort) { this.owner = owner; this.storage = storage; this.key = originalStorageKey(owner); }
  private write(key: string, value: unknown) {
    try {
      const serialized = JSON.stringify(value);
      this.storage.setItem(key, serialized);
      if (this.storage.getItem(key) !== serialized) throw new Error('readback');
    } catch { throw new TopicError('local_storage_unconfirmed'); }
  }
  read(): SavedOperation | null {
    try {
      const raw = this.storage.getItem(this.key);
      if (!raw) return null;
      const item = JSON.parse(raw);
      if (item.owner !== this.owner || !validCommand(item.body)) throw new Error('invalid operation');
      return item;
    } catch { throw new TopicError('local_operation_unreadable'); }
  }
  prepare(body: OriginalCommand): SavedOperation {
    if (!validCommand(body)) throw new TopicError('invalid_operation');
    const prior = this.read();
    if (prior && JSON.stringify(prior.body) !== JSON.stringify(body)) throw new TopicError('operation_unresolved');
    const saved = prior || { owner: this.owner, body };
    this.write(this.key, saved);
    return this.read()!;
  }
  recover(rows: OriginalPending[]) {
    if (rows.length > 1) throw new TopicError('operation_unresolved');
    const prior = this.read();
    if (!rows.length) return prior; // Absence never proves a dispatched request failed.
    const row = rows[0], body = { ...row.request, action: row.action } as OriginalCommand;
    if (!validCommand(body)) throw new TopicError('source_contract_mismatch');
    if (prior) {
      const same = Object.keys(prior.body).length === Object.keys(body).length
        && Object.entries(prior.body).every(([key, value]) => body[key] === value);
      if (!same) throw new TopicError('operation_unresolved');
      return prior;
    }
    return this.prepare(body);
  }
  record(saved: SavedOperation, receipt: OriginalReceipt) {
    if (JSON.stringify(this.read()?.body) !== JSON.stringify(saved.body)) throw new TopicError('operation_unresolved');
    this.write(this.key, { ...saved, receipt });
    return this.read()!;
  }
  release(saved: SavedOperation) {
    const current = this.read();
    if (!current || current.body.idempotencyKey !== saved.body.idempotencyKey || current.receipt?.operation.state !== 'completed'
      || current.receipt.sourceCommitted || !(current.receipt.ok === true || (current.body.action === 'complete' && current.receipt.localCommitted === true))) throw new TopicError('operation_unresolved');
    try { this.storage.removeItem(this.key); if (this.storage.getItem(this.key) !== null) throw new Error('readback'); }
    catch { throw new TopicError('local_storage_unconfirmed'); }
  }
  draft(context: string): Record<string, string> {
    try {
      const value = JSON.parse(this.storage.getItem(`${this.key}:draft:${context}`) || '{}');
      if (!value || typeof value !== 'object' || Array.isArray(value) || Object.values(value).some(v => typeof v !== 'string')) throw new Error('invalid draft');
      return value;
    } catch { throw new TopicError('local_draft_unreadable'); }
  }
  saveDraft(context: string, values: Record<string, string>) { this.write(`${this.key}:draft:${context}`, values); }
}

export function operationNotice(receipt?: OriginalReceipt) {
  if (!receipt) return '這次送出尚未確認。原文與請求已保留；查詢或重試會沿用同一請求。';
  if (receipt.sourceCommitted) return '原始學習紀錄已保存，但模型回覆的保存證據尚未確認。請保留這次請求，暫停後續作答。';
  if (receipt.localCommitted) return '結語與學程已在夫子保存，修業同步待確認。可明確重試同步，不會再推進一次學程。';
  if (receipt.pending || receipt.operation.state === 'running') return '這次請求仍待確認。請稍後查詢；不會另開請求或再次要求模型生成。';
  if (receipt.ok === true) return receipt.degraded ? `已保存本地備用回覆。${receipt.degradedReason || ''}` : '這一步已由夫子確認保存。';
  return '夫子尚未確認這一步成功，已保留原請求與回執。';
}
